import { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Save, Send, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrgReportForm() {
  const { meetingId, orgId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meeting, setMeeting] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [existingReport, setExistingReport] = useState(null);
  const [actions, setActions] = useState([]);
  
  const [report, setReport] = useState({
    tasks_completed: '',
    plans_next_month: '',
    issues_risks: [],
    milestones: [],
    path_to_green: '',
    action_updates: [],
    additional_notes: '',
    overall_rag_status: 'green'
  });

  useEffect(() => {
    fetchData();
  }, [meetingId, orgId]);

  const fetchData = async () => {
    try {
      const [meetingRes, orgRes, reportsRes, actionsRes] = await Promise.all([
        axios.get(`${API}/scheduled-meetings/${meetingId}`),
        axios.get(`${API}/organizations/${orgId}`),
        axios.get(`${API}/org-reports?meeting_id=${meetingId}&org_id=${orgId}`),
        axios.get(`${API}/actions`)
      ]);
      
      setMeeting(meetingRes.data);
      setOrganization(orgRes.data);
      setActions(actionsRes.data.filter(a => a.status !== 'Completed'));
      
      // Load existing report if any
      const existing = reportsRes.data.find(r => 
        r.scheduled_meeting_id === meetingId && r.organization_id === orgId
      );
      if (existing) {
        setExistingReport(existing);
        setReport({
          tasks_completed: existing.tasks_completed || '',
          plans_next_month: existing.plans_next_month || '',
          issues_risks: existing.issues_risks || [],
          milestones: existing.milestones || [],
          path_to_green: existing.path_to_green || '',
          action_updates: existing.action_updates || [],
          additional_notes: existing.additional_notes || '',
          overall_rag_status: existing.overall_rag_status || 'green'
        });
      }
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const addIssueRisk = () => {
    setReport({
      ...report,
      issues_risks: [...report.issues_risks, { description: '', rag_status: 'green', mitigation: '' }]
    });
  };

  const updateIssueRisk = (index, field, value) => {
    const updated = [...report.issues_risks];
    updated[index][field] = value;
    setReport({ ...report, issues_risks: updated });
  };

  const removeIssueRisk = (index) => {
    setReport({
      ...report,
      issues_risks: report.issues_risks.filter((_, i) => i !== index)
    });
  };

  const addMilestone = () => {
    setReport({
      ...report,
      milestones: [...report.milestones, { description: '', target_date: '', status: 'on_track' }]
    });
  };

  const updateMilestone = (index, field, value) => {
    const updated = [...report.milestones];
    updated[index][field] = value;
    setReport({ ...report, milestones: updated });
  };

  const removeMilestone = (index) => {
    setReport({
      ...report,
      milestones: report.milestones.filter((_, i) => i !== index)
    });
  };

  const addActionUpdate = () => {
    setReport({
      ...report,
      action_updates: [...report.action_updates, { action_id: '', update_text: '' }]
    });
  };

  const updateActionUpdate = (index, field, value) => {
    const updated = [...report.action_updates];
    updated[index][field] = value;
    setReport({ ...report, action_updates: updated });
  };

  const removeActionUpdate = (index) => {
    setReport({
      ...report,
      action_updates: report.action_updates.filter((_, i) => i !== index)
    });
  };

  const handleSave = async (submit = false) => {
    setSaving(true);
    try {
      const payload = {
        ...report,
        organization_id: orgId,
        scheduled_meeting_id: meetingId
      };

      if (existingReport) {
        await axios.put(`${API}/org-reports/${existingReport.id}`, payload);
        if (submit) {
          await axios.post(`${API}/org-reports/${existingReport.id}/submit`);
        }
      } else {
        const res = await axios.post(`${API}/org-reports`, payload);
        if (submit) {
          await axios.post(`${API}/org-reports/${res.data.id}/submit`);
        }
      }

      toast.success(submit ? 'Report submitted successfully' : 'Report saved as draft');
      if (submit) {
        navigate('/meeting-management');
      } else {
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to save report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const getRagColor = (rag) => {
    switch (rag) {
      case 'green': return 'bg-green-500';
      case 'amber': return 'bg-amber-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in max-w-4xl mx-auto" data-testid="org-report-form">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {existingReport?.status === 'submitted' && (
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            Submitted
          </span>
        )}
      </div>

      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Monthly Report</h1>
        <p className="text-lg text-gray-600">{organization?.name}</p>
        <p className="text-sm text-gray-500">For: {meeting?.title}</p>
      </div>

      {/* Overall RAG Status */}
      <Card>
        <CardHeader>
          <CardTitle>Overall Status</CardTitle>
          <CardDescription>Select the overall RAG status for this month</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            {['green', 'amber', 'red'].map((rag) => (
              <button
                key={rag}
                type="button"
                onClick={() => setReport({ ...report, overall_rag_status: rag })}
                className={`flex-1 p-4 rounded-lg border-2 transition ${
                  report.overall_rag_status === rag 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-8 h-8 rounded-full mx-auto mb-2 ${getRagColor(rag)}`} />
                <p className="font-medium capitalize">{rag}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tasks Completed */}
      <Card>
        <CardHeader>
          <CardTitle>Tasks Completed This Month</CardTitle>
          <CardDescription>What has been achieved since the last meeting?</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={report.tasks_completed}
            onChange={(e) => setReport({ ...report, tasks_completed: e.target.value })}
            placeholder="• Completed maintenance work on...&#10;• Resolved issue with...&#10;• Finished project..."
            rows={6}
          />
        </CardContent>
      </Card>

      {/* Plans for Next Month */}
      <Card>
        <CardHeader>
          <CardTitle>Plans for Next Month</CardTitle>
          <CardDescription>What activities are planned?</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={report.plans_next_month}
            onChange={(e) => setReport({ ...report, plans_next_month: e.target.value })}
            placeholder="• Schedule maintenance for...&#10;• Begin work on...&#10;• Review and update..."
            rows={6}
          />
        </CardContent>
      </Card>

      {/* Issues and Risks */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Issues & Risks</CardTitle>
              <CardDescription>Current issues or risks with RAG status</CardDescription>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addIssueRisk}>
              <Plus className="w-4 h-4 mr-1" />
              Add Issue
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {report.issues_risks.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No issues or risks to report</p>
          ) : (
            report.issues_risks.map((issue, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1 grid grid-cols-4 gap-3">
                    <div className="col-span-2">
                      <Label>Issue Description</Label>
                      <Input
                        value={issue.description}
                        onChange={(e) => updateIssueRisk(index, 'description', e.target.value)}
                        placeholder="Describe the issue..."
                      />
                    </div>
                    <div>
                      <Label>RAG Status</Label>
                      <Select value={issue.rag_status} onValueChange={(v) => updateIssueRisk(index, 'rag_status', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="green">🟢 Green</SelectItem>
                          <SelectItem value="amber">🟡 Amber</SelectItem>
                          <SelectItem value="red">🔴 Red</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => removeIssueRisk(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Mitigation / Action</Label>
                  <Input
                    value={issue.mitigation}
                    onChange={(e) => updateIssueRisk(index, 'mitigation', e.target.value)}
                    placeholder="What is being done to address this?"
                  />
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Milestones */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Key Milestones</CardTitle>
              <CardDescription>Important dates and deliverables</CardDescription>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addMilestone}>
              <Plus className="w-4 h-4 mr-1" />
              Add Milestone
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {report.milestones.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No milestones to report</p>
          ) : (
            report.milestones.map((milestone, index) => (
              <div key={index} className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label>Description</Label>
                  <Input
                    value={milestone.description}
                    onChange={(e) => updateMilestone(index, 'description', e.target.value)}
                    placeholder="Milestone description..."
                  />
                </div>
                <div className="w-40">
                  <Label>Target Date</Label>
                  <Input
                    type="date"
                    value={milestone.target_date}
                    onChange={(e) => updateMilestone(index, 'target_date', e.target.value)}
                  />
                </div>
                <div className="w-32">
                  <Label>Status</Label>
                  <Select value={milestone.status} onValueChange={(v) => updateMilestone(index, 'status', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="on_track">On Track</SelectItem>
                      <SelectItem value="at_risk">At Risk</SelectItem>
                      <SelectItem value="delayed">Delayed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => removeMilestone(index)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Path to Green */}
      {(report.overall_rag_status === 'amber' || report.overall_rag_status === 'red') && (
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Path to Green
            </CardTitle>
            <CardDescription>What actions are needed to return to green status?</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={report.path_to_green}
              onChange={(e) => setReport({ ...report, path_to_green: e.target.value })}
              placeholder="Describe the steps and timeline to resolve current issues..."
              rows={4}
            />
          </CardContent>
        </Card>
      )}

      {/* Action Updates */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Action Updates</CardTitle>
              <CardDescription>Progress updates on assigned actions</CardDescription>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addActionUpdate}>
              <Plus className="w-4 h-4 mr-1" />
              Add Update
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {report.action_updates.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No action updates</p>
          ) : (
            report.action_updates.map((update, index) => (
              <div key={index} className="flex gap-3 items-start p-3 border rounded">
                <div className="w-48">
                  <Label>Action</Label>
                  <Select value={update.action_id} onValueChange={(v) => updateActionUpdate(index, 'action_id', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select action..." />
                    </SelectTrigger>
                    <SelectContent>
                      {actions.map((action) => (
                        <SelectItem key={action.id} value={action.id}>
                          {action.reference}: {action.title.substring(0, 30)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label>Update</Label>
                  <Textarea
                    value={update.update_text}
                    onChange={(e) => updateActionUpdate(index, 'update_text', e.target.value)}
                    placeholder="Progress update..."
                    rows={2}
                  />
                </div>
                <Button type="button" variant="ghost" size="sm" className="text-red-500 mt-6" onClick={() => removeActionUpdate(index)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Additional Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Notes</CardTitle>
          <CardDescription>Any other information to share</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={report.additional_notes}
            onChange={(e) => setReport({ ...report, additional_notes: e.target.value })}
            placeholder="Any other relevant information..."
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Submit Buttons */}
      <div className="flex justify-end gap-3 pb-8">
        <Button type="button" variant="outline" onClick={() => handleSave(false)} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          Save Draft
        </Button>
        <Button 
          type="button" 
          onClick={() => handleSave(true)} 
          disabled={saving}
          className="bg-gradient-to-r from-blue-500 to-green-500"
        >
          <Send className="w-4 h-4 mr-2" />
          Submit Report
        </Button>
      </div>
    </div>
  );
}
